import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActionsSubject } from '@ngrx/store';
import moment from 'moment/moment';
import { defer, merge } from 'rxjs';
import { filter, finalize, map, scan, startWith, switchMap } from 'rxjs/operators';
import {
  AvailableMacro,
  Fx,
  PatternDef,
  PatternDefPunch,
  IShapeAliasTable,
  IPatternMacroCaller,
  IPatternWhereUsedItem,
  MacroKind,
  ShapeGeometryKind,
  TriangleDirection,
} from '../../../app/core/dto';
import { PUT, DEL } from '../shared/services/clientData.actions';
import { Ams } from '../../../app/amsconfig';
import { SubscriptionService } from '../shared/services/subscription.service';

export interface ISolutionTree {
  patternHeader: { patternId: string; patternName: string };
  report: {
    machineNumber: number;
    pattern: { patternId: string; patternName: string };
    lengthIn: number;
    widthIn: number;
    duration: number;
    attemptedOn?: moment.Moment;
    status: string;
    jobs: { jobId: string; itemId: number }[];
    debugTable: string;
    toolingCodes: string[];
    /** The job material's gauge — part of the solution identity; passed to flatview so
     * gauge-matched geometry corrections resolve the same entry the solve used. 0 = unset. */
    gauge: number;
  }[];
}

/** A machine's tooling-code rules that move shape punches (flips, shape offsets, strip offset). */
export interface IToolingCodeEffect {
  machineNumber: number;
  toolingCodePatterns: string[];
}

/**
 * What a tool row can become on one or more machines: a shape punch per hole one hit of the tool
 * punches, as those machines' shape tables define the tool.
 */
export interface IToolToShapeCandidate {
  machines: number[];
  /** Each shape the tool punches and its offset from the tool's center. */
  holes: { shapeCode: string; xOffset: number; yOffset: number }[];
  /** The tool has no axis, so Y is where the machine has the tool, not the row's own Y. */
  yFromToolPosition: boolean;
  /** The tool rides an axis but the row has no Y reference, so its Y was kept as typed. */
  yUnknown: boolean;
  /**
   * In a macro: a Y that was on the strip (Center±, or a tool with no axis) is now Macro±,
   * measured from the call, which lands in the same place only for calls at Y 0.
   */
  yAssumesCallAtYZero: boolean;
  /** The shape is off the tool's center along X, but the row's X offsets are spacings. */
  xSpacingKept: boolean;
  /** Every tooling code flips or shifts the shape in X, but the row repeats, so X wasn't adjusted. */
  xFrameNotAdjusted: boolean;
  punches: PatternDefPunch[];
  /** Tooling codes under which the converted row lands somewhere other than the tool row. */
  toolingCodeEffects: IToolingCodeEffect[];
  /** Catch-all rules (every tooling code) whose flips and offsets the row accounts for. */
  frameRules: IToolingCodeEffect[];
  /** Shape aliases that stand for the same holes, one punch per punch of the row. */
  aliases: {
    aliasName: string;
    shapeCount: number;
    hasOffsets: boolean;
    punches: PatternDefPunch[];
    toolingCodeEffects: IToolingCodeEffect[];
    frameRules: IToolingCodeEffect[];
  }[];
}

export interface IToolToShapeResult {
  toolId: number;
  /** Other rows with this tool form an even-spacing or proportional group. */
  inRepeatingGroup: boolean;
  /** Machines where the tool punches several holes per hit, left out because the row repeats. */
  multiHoleSkippedOn: number[];
  /** In a macro: machines left out because a hole's Y would be measured from a strip edge. */
  macroEdgeSkippedOn: number[];
  /** In a macro: the patterns that call it somewhere other than Y 0. */
  callersNotAtYZero: string[];
  candidates: IToolToShapeCandidate[];
}

/** A tool macro saved as a shape macro, with the patterns that call it moved along. */
export interface IShapeMacroConversion {
  pattern: PatternDef;
  updatedCallers: string[];
  /** Callers whose own save failed after the macro's, each with the reason. */
  callerFailures: string[];
}

export interface IFlatViewHole {
  x: number;
  y: number;
  shapeCode: string | null;
  originalShapeCode: string | null;
  toolId: number | null;
  /**
   * Drawn-hole geometry from the shape geometry library, keyed by the resolved machine shape
   * code. Null = no entry — render the nominal marker. Direction is as-mounted; the per-hole
   * flipY still applies on top (XOR) at render time.
   */
  geometry: {
    kind: ShapeGeometryKind;
    xIn: number;
    yIn: number;
    direction: TriangleDirection;
  } | null;
  flipX: boolean;
  flipY: boolean;
  segment: {
    name: string;
    canonicalName: string;
    anchor: string;
    yOffset: number;
    preFlipY: number | null;
  } | null;
  /** Set when the hole was exploded from a shape-macro call: the macro and its row. */
  macro: { name: string; row: number } | null;
}

export interface IFlatView {
  patternName: string;
  machineNumber: number;
  toolingCode: string | null;
  gauge: number | null;
  lengthIn: number;
  widthIn: number | null;
  stripOffset: number;
  status: string | null;
  error: string | null;
  partial: boolean;
  appliedFlipX: boolean | null;
  appliedFlipY: boolean | null;
  geometry: {
    /**
     * Segments = the resolution's own profile geometry (segment-referenced pattern).
     * Tooling = machine-referenced pattern; the server derived the profile from the tooling's
     * nominal dimensions (plus any resolvable corrections) for the fold only — badge it.
     */
    source: 'Segments' | 'Tooling';
    finWidth: number | null;
    flatWebWidth: number;
    /** Effective per-side corrections from the v2 (gauge, side, direction) cells. */
    bendCorrectionPlus: number;
    bendCorrectionMinus: number;
    /** Gauge of the matched cell per side; null = gauge-agnostic cell or no cell. */
    cellGaugePlus: number | null;
    cellGaugeMinus: number | null;
    flangeHeightPlus: number;
    flangeHeightMinus: number;
    webCenterOffset: number;
    webCenterOffsetIsOverride: boolean;
    /** The direction rule that supplied the Δ override, when one did. */
    webCenterOffsetRule: string | null;
    bendAllowance: number;
  } | null;
  /**
   * Flange bend directions for the 3D formed view, machine-run frame ("as the part exits").
   * Cascade over the machine's direction rules (most specific non-null value per field).
   * Null = no rule matched — the 3D view renders legs-up and badges it. (Rendering may
   * default; the solve fails loud instead when corrections need the direction.)
   */
  directions: {
    flangePlus: 'Up' | 'Down' | null;
    flangeMinus: 'Up' | 'Down' | null;
    /** The rule pattern that supplied each side, for the annotation. */
    entryPlus: string | null;
    entryMinus: string | null;
  } | null;
  /**
   * Shape punches the machine's ignore list dropped (keyed by the pattern's own code, count =
   * holes after alias expansion). Not drawn — they have no tool on this machine.
   */
  ignoredShapes?: { shapeCode: string; count: number }[];
  /**
   * Shape macros the machine ignores, with the number of holes dropped from each (after alias
   * expansion). Not drawn, and not counted in ignoredShapes.
   */
  ignoredMacros?: { macroName: string; count: number }[];
  /** Every shape punch was ignored: nothing to solve, so no holes and no error. */
  allShapesIgnored?: boolean;
  /**
   * Set when the pattern is a macro: it is drawn as one call at callX (the middle of the blank,
   * which no flip moves), Y Default — the material center for a relocatable macro, the rows'
   * own segments for a placed one.
   */
  macroInstance?: { kind: MacroKind; callX: number } | null;
  guides: { key: string; y: number }[];
  holes: IFlatViewHole[];
}

@Injectable({ providedIn: 'root' })
export class PatternsApiService {
  constructor(
    private http: HttpClient,
    private subscriptionService: SubscriptionService,
    private actionsSub: ActionsSubject,
  ) {}

  getSolutions(patternId: string) {
    // The realtime updates are wired up this way, outside the ClientDataStore,
    // just as an experiment. Want to see what it entails to stop using that
    // infrastructure.

    // Collect those updates.
    const updates$ = this.actionsSub.pipe(
      filter((a) => a.type === PUT),
      filter((a: any) => a.collection === 'SolutionTree'),
      filter((a: { payload: ISolutionTree }) => a.payload.patternHeader.patternId === patternId),
      map((a) => a.payload),
    );

    // When orphaned solutions are cleaned up, the server sends ObjectDelete.
    const deletes$ = this.actionsSub.pipe(
      filter((a) => a.type === DEL),
      filter((a: any) => a.collection === 'SolutionTree'),
      filter((a: any) => {
        const id = a.id as string;
        const delPatternId = id.indexOf('/') > 0 ? id.substring(id.indexOf('/') + 1) : id;
        return delPatternId === patternId;
      }),
      map(() => ({ patternHeader: { patternId, patternName: '' }, report: [] }) as ISolutionTree),
    );

    // The server keys its subscriptions by id, so the very object that was added has to be the
    // one deleted - a freshly built Fx.Subscription carries a new uuid and would leave the
    // server-side subscription alive forever. Creating it inside `defer` and releasing it in
    // `finalize` keeps add and delete paired with the lifetime of each subscriber.
    return defer(() => {
      const subscription = new Fx.Subscription('SolutionTree', new Fx.In('patternId', [patternId]));
      this.subscriptionService.addSubscription(subscription);

      return merge(
        this.http.get<ISolutionTree>(
          `${Ams.Config.BASE_URL}/_api/punchpatterns/${patternId}/solutions`,
        ),
        updates$,
        deletes$,
      ).pipe(finalize(() => this.subscriptionService.deleteSubscription(subscription)));
    }).pipe(
      map((solutions) => {
        return {
          ...solutions,
          report: solutions.report.map((s) => ({
            ...s,
            attemptedOn: s.attemptedOn != null ? moment(s.attemptedOn) : null,
          })),
        };
      }),
    );
  }

  getPunchPatterns(patternId: string) {
    return this.http.get<PatternDef>(`${Ams.Config.BASE_URL}/_api/punchpatterns/${patternId}`);
  }

  // The flat blank exactly as the solver receives it (real solve path, stopped at the
  // FindSolution boundary), plus guide lines and per-hole segment provenance.
  getFlatView(
    patternId: string,
    machineNumber: number,
    toolingCode: string | null,
    lengthIn: number,
    widthIn: number,
    gauge: number | null = null,
  ) {
    const params: { [k: string]: string } = {
      lengthIn: String(lengthIn),
      widthIn: String(widthIn),
    };
    if (toolingCode) {
      params.toolingCode = toolingCode;
    }
    if (gauge) {
      params.gauge = String(gauge);
    }
    return this.http.get<IFlatView>(
      `${Ams.Config.BASE_URL}/_api/punchpatterns/${patternId}/flatview/${machineNumber}`,
      { params },
    );
  }

  getAvailableMacros(patternId: string) {
    return this.http.get<AvailableMacro[]>(
      `${Ams.Config.BASE_URL}/_api/punchpatterns/${patternId}/availableMacros`,
    );
  }

  /** The patterns that call this macro; empty for a pattern that isn't a macro. */
  getMacroCallers(patternId: string) {
    return this.http.get<IPatternMacroCaller[]>(
      `${Ams.Config.BASE_URL}/_api/punchpatterns/${patternId}/callers`,
    );
  }

  /**
   * The shape punches that stand for one shape-macro call (the editor's Explode action). The
   * server does the reference math and saves nothing; a 400 carries the reason the call can't be
   * exploded. `patternPunches` are the pattern's other rows, which decide whether the call is part
   * of an even-spacing group.
   */
  explodeMacroCall(calls: PatternDefPunch[], patternPunches: PatternDefPunch[]) {
    return this.http.post<{ punches: PatternDefPunch[] }>(
      `${Ams.Config.BASE_URL}/_api/punchpatterns/explodeMacroCall`,
      { calls, patternPunches },
    );
  }

  /**
   * The shapes one tool row can become (the editor's Convert to Shape action): one candidate per
   * shape a machine's shape table defines on the tool, with the row's offsets moved so the hole
   * stays where the tool puts it. Saves nothing; a 400 carries the reason the row can't convert.
   * `macro` is set when the row is in a macro pattern: its rows are then placed from the call,
   * and `macroName` (the saved name; empty for a new macro) finds the patterns that call it.
   */
  convertToolToShape(
    punches: PatternDefPunch[],
    patternPunches: PatternDefPunch[],
    macro?: { macroName: string },
  ) {
    return this.http.post<IToolToShapeResult>(
      `${Ams.Config.BASE_URL}/_api/punchpatterns/toolToShape`,
      { punches, patternPunches, inMacro: !!macro, macroName: macro?.macroName ?? '' },
    );
  }

  /**
   * Saves a tool macro whose rows are now shapes as a shape macro, and re-saves every pattern
   * that calls it so its calls are shape-macro calls. A 400 lists why nothing was saved.
   */
  convertToShapeMacro(patternId: string, pattern: PatternDef) {
    return this.http.post<IShapeMacroConversion>(
      `${Ams.Config.BASE_URL}/_api/punchpatterns/${patternId}/convertToShapeMacro`,
      pattern,
    );
  }

  getAllSolutions() {
    // SignalR sends individual SolutionTree objects (not arrays) when solutions change.
    const updates$ = this.actionsSub.pipe(
      filter((a) => a.type === PUT),
      filter((a: any) => a.collection === 'SolutionTree'),
      map((a: { payload: ISolutionTree }) => ({ type: 'put' as const, payload: a.payload })),
    );

    // When orphaned solutions are cleaned up, the server sends ObjectDelete.
    const deletes$ = this.actionsSub.pipe(
      filter((a) => a.type === DEL),
      filter((a: any) => a.collection === 'SolutionTree'),
      map((a: any) => ({ type: 'del' as const, id: a.id as string })),
    );

    // Load the full list via HTTP, then merge in individual SignalR updates and deletes.
    // Subscribe to updates per subscriber, and delete that exact subscription on teardown -
    // the server frees subscriptions by id, so it has to be this object and not a copy.
    return defer(() => {
      const subscription = new Fx.Subscription('SolutionTree', new Fx.All());
      this.subscriptionService.addSubscription(subscription);

      return this.http
        .get<ISolutionTree[]>(`${Ams.Config.BASE_URL}/_api/punchpatterns/allsolutions`)
        .pipe(
          switchMap((initialSolutions) =>
            merge(updates$, deletes$).pipe(
              scan((acc, event) => {
                if (event.type === 'put') {
                  const idx = acc.findIndex(
                    (s) => s.patternHeader.patternId === event.payload.patternHeader.patternId,
                  );
                  if (idx >= 0) {
                    const newAcc = [...acc];
                    newAcc[idx] = event.payload;
                    return newAcc;
                  }
                  return [...acc, event.payload];
                } else {
                  // Delete: id format is "SolutionTree/{patternId}"
                  const patternId =
                    event.id.indexOf('/') > 0
                      ? event.id.substring(event.id.indexOf('/') + 1)
                      : event.id;
                  return acc.filter((s) => s.patternHeader.patternId !== patternId);
                }
              }, initialSolutions),
              startWith(initialSolutions),
            ),
          ),
          map((solutions) =>
            solutions.map((solution) => ({
              ...solution,
              report: solution.report.map((s) => ({
                ...s,
                attemptedOn: s.attemptedOn != null ? moment(s.attemptedOn) : null,
              })),
            })),
          ),
          finalize(() => this.subscriptionService.deleteSubscription(subscription)),
        );
    });
  }

  addPattern(pattern: PatternDef) {
    return this.http.put<PatternDef>(Ams.Config.BASE_URL + '/_api/punchpatterns', pattern);
  }

  setPermanent(patternIds: string[], isPermanent: boolean) {
    return this.http.post<{ updated: number; notFound: string[] }>(
      `${Ams.Config.BASE_URL}/_api/punchpatterns/permanent`,
      { patternIds, isPermanent },
    );
  }

  updatePattern(patternId: string, pattern: PatternDef) {
    return this.http.put<PatternDef>(
      `${Ams.Config.BASE_URL}/_api/punchpatterns/${patternId}`,
      pattern,
    );
  }

  getShapealiastable() {
    return this.http.get<IShapeAliasTable>(`${Ams.Config.BASE_URL}/_api/shapealiastable`);
  }

  getAvailableSegments() {
    return this.http.get<string[]>(`${Ams.Config.BASE_URL}/_api/availableSegments`);
  }

  saveShapealiastable(data: IShapeAliasTable) {
    return this.http.put(`${Ams.Config.BASE_URL}/_api/shapealiastable`, data);
  }

  getWhereUsed(patternId: string) {
    return this.http.get<IPatternWhereUsedItem[]>(
      `${Ams.Config.BASE_URL}/_api/punchpatterns/${patternId}/whereUsed`,
    );
  }

  getDuplicates() {
    return this.http.get<IDuplicatePatternGroup[]>(
      `${Ams.Config.BASE_URL}/_api/punchpatterns/duplicates`,
    );
  }

  /**
   * `recordNumber` is the row's physical DBF record (VFP RECNO()). Name + changeId alone cannot
   * single out rows that share both, which is what a same-case duplicate group looks like once
   * one name-keyed write has stamped the whole group with a single changeId.
   */
  resolveDuplicate(patternName: string, changeId: number, recordNumber: number) {
    return this.http.post<{ deleted: string }>(
      `${Ams.Config.BASE_URL}/_api/punchpatterns/duplicates/resolve`,
      { patternName, changeId, recordNumber },
    );
  }
}

export interface IDuplicatePatternGroup {
  key: string;
  rows: IDuplicatePatternRow[];
}

export interface IDuplicatePatternRow {
  patternName: string;
  changeId: number;
  /** VFP RECNO() of this row - the only pin that separates rows sharing name and changeId. */
  recordNumber: number;
  hash: string;
  patternNumber: number;
  isMacro: boolean;
  isPermanent: boolean;
  importDate?: string;
  lastUsedDate?: string;
  punchCount: number;
  orderItemCount: number;
  isSyncedToPro: boolean;
}
